import {
  cloneElement,
  createContext,
  createElement,
  isValidElement,
  useContext,
  useLayoutEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

/*
 * Live-change helpers (spec 07 › Movimiento, LiveScope): what moves is a
 * change the person can see happen, never a first paint, a remount, a
 * restored list or another subject (J/K to the next offer, another thread).
 * Pure in render (derived state, never a ref written while rendering), so
 * StrictMode and concurrent renders never read a false «changed».
 *
 * They compare with `Object.is` and update their state while rendering:
 * values, subject keys and reset keys are primitives (an id, a count, a
 * string) or stable references (a store's object, a memoized value). One
 * built in the caller's render (`[a, b]`, `{ … }`) differs on every render,
 * and the component re-renders until React gives up («Too many re-renders»):
 * pass a key built from it (`${a}:${b}`) instead.
 */

/**
 * `true` from the render where `value` changed after the mount, for the same
 * `subjectKey`, until the next change: the value on screen arrived live and
 * may animate. The mount, and a change of subject (with or without a new
 * value), read `false`. Act on the change itself (a key, an effect on
 * `value`) and read this to know whether it may move. Both are primitives or
 * stable references (see above).
 */
export function useLiveChange(value: unknown, subjectKey?: unknown): boolean {
  const [seen, setSeen] = useState({ value, subjectKey, live: false });
  if (!Object.is(seen.value, value) || !Object.is(seen.subjectKey, subjectKey)) {
    const live = Object.is(seen.subjectKey, subjectKey);
    setSeen({ value, subjectKey, live });
    return live;
  }
  return seen.live;
}

const LiveContext = createContext(false);

/**
 * Whether this component mounted inside a `LiveScope` that was already live:
 * its entrance may play (an `appear="live"` Collapse, a wash, a number that
 * rolls in). Fixed at the mount; outside any scope, `false`.
 */
export function useLiveInsert(): boolean {
  const live = useContext(LiveContext);
  const [atMount] = useState(live);
  return atMount;
}

export interface LiveScopeProps {
  /**
   * Put `data-live` on the one child element instead of a `display: contents`
   * wrapper (a list or a panel that is already the surface's root).
   */
  asChild?: boolean;
  children: ReactNode;
}

/**
 * The surface that owns its motion (a route's root, a list, a panel, a sheet)
 * goes live one frame after its first layout effect: `data-live` on its node,
 * and `useLiveInsert()` for its descendants. What was there when it mounted
 * took its first style without it, so an `enter-*` (gated on
 * `:root[data-motion-ready] [data-live]`) or a live insert never plays on a
 * mount, a back or a restore; what is inserted after does. Without frames or
 * Web Animations (jsdom), live in the first effect.
 */
export function LiveScope({ asChild = false, children }: LiveScopeProps) {
  const [live, setLive] = useState(false);
  useLayoutEffect(() => {
    const frames = typeof requestAnimationFrame === "function" && typeof Element.prototype.getAnimations === "function";
    if (!frames) {
      // Nothing animates here (jsdom): live in the first effect, still after the mount's own render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLive(true);
      return;
    }
    const frame = requestAnimationFrame(() => setLive(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  const mark = { "data-live": live ? "" : undefined };
  const node =
    asChild && isValidElement(children)
      ? cloneElement(children as ReactElement<{ "data-live"?: string }>, mark)
      : createElement("div", { className: "contents", ...mark }, children);
  return createElement(LiveContext.Provider, { value: live }, node);
}
